const USB_VENDOR_KEY = "usb_printer_vendor_id";
const USB_PRODUCT_KEY = "usb_printer_product_id";
const USB_SERIAL_KEY = "usb_printer_serial_number";
const USB_LABEL_KEY = "usb_printer_connected";

const USB_STATE = {
  device: null,
  interfaceNumber: null,
  endpointNumber: null,
  packetSize: 64,
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const emitStatus = (onStatus, message) => {
  if (typeof onStatus === "function" && message) {
    onStatus(message);
  }
};

const canUseStorage = () =>
  typeof window !== "undefined" && Boolean(window?.localStorage);

const readStoredInt = (key) => {
  if (!canUseStorage()) return null;
  const raw = window.localStorage.getItem(key);
  if (raw === null || raw === "") return null;
  const value = Number(raw);
  return Number.isInteger(value) ? value : null;
};

const readStoredString = (key) => {
  if (!canUseStorage()) return "";
  return String(window.localStorage.getItem(key) || "").trim();
};

const readStoredUsbDescriptor = () => ({
  vendorId: readStoredInt(USB_VENDOR_KEY),
  productId: readStoredInt(USB_PRODUCT_KEY),
  serialNumber: readStoredString(USB_SERIAL_KEY),
  label: readStoredString(USB_LABEL_KEY),
});

const formatHex = (value) => {
  if (!Number.isInteger(Number(value))) return "";
  return `0x${Number(value).toString(16).padStart(4, "0")}`;
};

export const getUsbPrinterLabel = (device) => {
  const direct = String(
    device?.productName || device?.manufacturerName || device?.serialNumber || ""
  ).trim();
  if (direct) return direct;
  const vendor = formatHex(device?.vendorId);
  const product = formatHex(device?.productId);
  if (vendor || product) {
    return `USB Printer ${vendor}${vendor && product ? ":" : ""}${product}`.trim();
  }
  return "USB Printer";
};

export const rememberUsbDevice = (device) => {
  if (!canUseStorage() || !device) return;
  try {
    if (Number.isInteger(Number(device.vendorId))) {
      window.localStorage.setItem(USB_VENDOR_KEY, String(device.vendorId));
    }
    if (Number.isInteger(Number(device.productId))) {
      window.localStorage.setItem(USB_PRODUCT_KEY, String(device.productId));
    }
    if (device.serialNumber) {
      window.localStorage.setItem(USB_SERIAL_KEY, String(device.serialNumber));
    }
    window.localStorage.setItem(USB_LABEL_KEY, getUsbPrinterLabel(device));
  } catch {
    // ignore storage errors
  }
};

const matchesStoredUsbDevice = (device, descriptor) => {
  if (!device || !descriptor) return false;
  if (
    descriptor.vendorId !== null &&
    Number(device.vendorId) !== Number(descriptor.vendorId)
  ) {
    return false;
  }
  if (
    descriptor.productId !== null &&
    Number(device.productId) !== Number(descriptor.productId)
  ) {
    return false;
  }
  if (
    descriptor.serialNumber &&
    String(device.serialNumber || "") !== String(descriptor.serialNumber)
  ) {
    return false;
  }
  return descriptor.vendorId !== null || descriptor.productId !== null;
};

const clearUsbState = () => {
  USB_STATE.device = null;
  USB_STATE.interfaceNumber = null;
  USB_STATE.endpointNumber = null;
  USB_STATE.packetSize = 64;
};

export const resetUsbPrinterConnection = async () => {
  const active = USB_STATE.device;
  clearUsbState();
  if (active?.opened) {
    try {
      await active.close();
    } catch {
      // ignore close errors
    }
  }
};

const chunkBytes = (bytes, size) => {
  const chunks = [];
  for (let index = 0; index < bytes.length; index += size) {
    chunks.push(bytes.slice(index, index + size));
  }
  return chunks;
};

const getUsbWriteTargets = (device) => {
  const configs =
    Array.isArray(device?.configurations) && device.configurations.length
      ? device.configurations
      : device?.configuration
      ? [device.configuration]
      : [];

  const targets = [];
  configs.forEach((config) => {
    (config?.interfaces || []).forEach((iface) => {
      (iface?.alternates || []).forEach((alt) => {
        const endpoint = (alt?.endpoints || []).find(
          (entry) =>
            entry?.direction === "out" &&
            (entry?.type === "bulk" || entry?.type === "interrupt")
        );
        if (!endpoint) return;
        targets.push({
          configurationValue: config.configurationValue,
          interfaceNumber: iface.interfaceNumber,
          alternateSetting: alt.alternateSetting,
          endpointNumber: endpoint.endpointNumber,
          packetSize: endpoint.packetSize || 64,
          endpointType: endpoint.type || "bulk",
          interfaceClass: alt.interfaceClass,
          interfaceSubclass: alt.interfaceSubclass,
          interfaceProtocol: alt.interfaceProtocol,
        });
      });
    });
  });

  return targets.sort((left, right) => {
    const score = (item) => {
      let total = 0;
      if (item.interfaceClass === 0x07) total += 100;
      if (item.interfaceClass === 0xff) total += 70;
      if (item.endpointType === "bulk") total += 20;
      if (item.alternateSetting === 0) total += 5;
      return total;
    };
    return score(right) - score(left);
  });
};

const normalizeUsbError = (error) => {
  const message = String(error?.message || error || "").trim();
  if (!message) return new Error("USB printer connection failed.");
  if (/claim/i.test(message) || /access denied/i.test(message) || /busy/i.test(message)) {
    return new Error(
      "USB printer interface busy hai. Printer driver ya dusri app ko close karke phir try karein."
    );
  }
  if (/networkerror/i.test(message) || /device unavailable/i.test(message)) {
    return new Error("USB printer disconnected lag raha hai. Reconnect karke phir try karein.");
  }
  return error instanceof Error ? error : new Error(message);
};

const prepareUsbDevice = async (device, { onStatus } = {}) => {
  if (!device) throw new Error("USB printer not found.");
  if (
    USB_STATE.device === device &&
    USB_STATE.endpointNumber !== null &&
    USB_STATE.interfaceNumber !== null &&
    device.opened
  ) {
    return USB_STATE;
  }

  try {
    emitStatus(onStatus, "Connecting USB printer...");
    if (!device.opened) {
      await device.open();
    }

    if (!device.configuration) {
      const firstConfig =
        Array.isArray(device.configurations) && device.configurations.length
          ? device.configurations[0]?.configurationValue
          : 1;
      await device.selectConfiguration(firstConfig || 1);
    }

    const target = getUsbWriteTargets(device)[0];
    if (!target) {
      throw new Error("No writable USB printer endpoint found on this device.");
    }

    if (device.configuration?.configurationValue !== target.configurationValue) {
      await device.selectConfiguration(target.configurationValue);
    }

    const currentInterface = () =>
      device.configuration?.interfaces?.find(
        (iface) => iface.interfaceNumber === target.interfaceNumber
      );

    if (!currentInterface()?.claimed) {
      await device.claimInterface(target.interfaceNumber);
    }

    if (
      currentInterface()?.alternate?.alternateSetting !== target.alternateSetting &&
      typeof device.selectAlternateInterface === "function"
    ) {
      await device.selectAlternateInterface(
        target.interfaceNumber,
        target.alternateSetting
      );
    }

    USB_STATE.device = device;
    USB_STATE.interfaceNumber = target.interfaceNumber;
    USB_STATE.endpointNumber = target.endpointNumber;
    USB_STATE.packetSize = target.packetSize || 64;
    rememberUsbDevice(device);
    emitStatus(onStatus, `Connected: ${getUsbPrinterLabel(device)}`);
    return USB_STATE;
  } catch (error) {
    if (USB_STATE.device === device) {
      clearUsbState();
    }
    if (device?.opened) {
      try {
        await device.close();
      } catch {
        // ignore close errors
      }
    }
    throw normalizeUsbError(error);
  }
};

const buildUsbRequestFilters = () => {
  const stored = readStoredUsbDescriptor();
  const filters = [];
  if (stored.vendorId !== null) {
    const preferred = { vendorId: stored.vendorId };
    if (stored.productId !== null) preferred.productId = stored.productId;
    if (stored.serialNumber) preferred.serialNumber = stored.serialNumber;
    filters.push(preferred);
  }
  filters.push({ classCode: 0x07 });
  filters.push({ classCode: 0xff });
  filters.push({});

  const unique = new Map();
  filters.forEach((filter) => {
    unique.set(JSON.stringify(filter), filter);
  });
  return Array.from(unique.values());
};

const sortUsbDevices = (devices, descriptor) => {
  const list = Array.isArray(devices) ? [...devices] : [];
  return list.sort((left, right) => {
    const leftMatch = matchesStoredUsbDevice(left, descriptor) ? 1 : 0;
    const rightMatch = matchesStoredUsbDevice(right, descriptor) ? 1 : 0;
    if (leftMatch !== rightMatch) return rightMatch - leftMatch;
    return getUsbPrinterLabel(left).localeCompare(getUsbPrinterLabel(right));
  });
};

export const connectUsbPrinter = async ({
  allowPrompt = true,
  onStatus,
} = {}) => {
  if (!navigator?.usb) {
    throw new Error("WebUSB not supported in this browser.");
  }

  if (
    USB_STATE.device &&
    USB_STATE.endpointNumber !== null &&
    USB_STATE.interfaceNumber !== null &&
    USB_STATE.device.opened
  ) {
    return USB_STATE;
  }

  const stored = readStoredUsbDescriptor();
  if (typeof navigator.usb.getDevices === "function") {
    try {
      const granted = await navigator.usb.getDevices();
      const ordered = sortUsbDevices(granted, stored);
      for (const device of ordered) {
        try {
          const prepared = await prepareUsbDevice(device, { onStatus });
          if (prepared) return prepared;
        } catch {
          // try next granted device
        }
      }
    } catch {
      // fallback to manual picker below
    }
  }

  if (!allowPrompt) {
    throw new Error("No paired USB printer found right now.");
  }

  emitStatus(onStatus, "Select USB printer...");
  const device = await navigator.usb.requestDevice({
    filters: buildUsbRequestFilters(),
  });
  return prepareUsbDevice(device, { onStatus });
};

export const writeUsb = async (
  bytes,
  { allowPrompt = true, onStatus } = {}
) => {
  const state = await connectUsbPrinter({ allowPrompt, onStatus });
  const transferSize = Math.max(
    64,
    Math.min(512, Math.max(64, Number(state.packetSize || 64) * 8))
  );
  const chunks = chunkBytes(bytes, transferSize);

  try {
    emitStatus(onStatus, "Printing via USB...");
    for (let index = 0; index < chunks.length; index += 1) {
      const result = await state.device.transferOut(
        state.endpointNumber,
        chunks[index]
      );
      if (result?.status && result.status !== "ok") {
        if (result.status === "stall" && state.device.clearHalt) {
          try {
            await state.device.clearHalt("out", state.endpointNumber);
          } catch {
            // ignore recovery errors
          }
        }
        throw new Error("USB printer transfer stalled.");
      }
      await sleep(index % 24 === 23 ? 10 : 2);
    }
    rememberUsbDevice(state.device);
  } catch (error) {
    await resetUsbPrinterConnection();
    throw normalizeUsbError(error);
  }
};
